/**
 * ManuallyAddPlayerModal Container Component
 *
 * Manages form state, validation, dropdown data fetching, and submission
 * for manually adding or editing a manual player.
 * When editData is provided, operates in edit mode with pre-filled form and update mutation.
 * When editData is absent, operates in create mode (original behavior).
 * @module components/ManuallyAddPlayerModal
 */

import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import * as yup from "yup";

import ManuallyAddPlayerModalView from "./ManuallyAddPlayerModalView";
import {
  IManuallyAddPlayerForm,
  ManualPlayerEditData,
  ManuallyAddPlayerModalContainerProps,
} from "@/types/manuallyAddPlayerModalTypes";
import {
  buildChangedFieldsInput,
  extractTeamName,
  isFutureDate,
  isValidDateString,
  splitSearchText,
} from "@/utils/manualPlayerUtils";

import {
  usePlayerDetailUpdateManualPlayerMutation,
  usePlayerSearchCreateManualPlayerMutation,
  useRosterManagementListMyOrganizationsEligibleTeamsQuery,
  useRosterManagementListNationalitiesQuery,
  useRosterManagementListPositionsQuery,
  useSquadPlanningUpsertOrganizationPlayerDataMutation,
} from "@/generated/graphql";
import { Platform } from "react-native";

import { nationalities, POSITIONS_FULL_LIST_FIRST } from "@/config/constants";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";
import { IOptionSelect } from "@/types/customSelectTypes";
import dedupePositionNodes from "@/utils/dedupePositionNodes";
import { flagUrlForNationality } from "@/utils/nationalityRegistry";

/** Debounce delay for team autocomplete search in ms */
const TEAM_SEARCH_DEBOUNCE_MS = 300;

/** Max results for team autocomplete dropdown */
const TEAM_SUGGESTIONS_LIMIT = 10;

/**
 * Max results for nationalities (load all for dropdown).
 *
 * Aligned with the `first: 1000` used by the other five `listNationalities`
 * consumers so this app-shell read shares one normalized cache entry instead of
 * fragmenting on a distinct `$first`. On the player-detail path this container
 * co-mounts (via GlobalSearchModal) with another nationalities reader; matching
 * `$first` lets Apollo query-dedupe + cache-first collapse them to a single wire
 * operation (SE-5064; cache-key fragmentation rule). Only ~225 nationalities
 * exist, so 1000 returns the identical full set — no behavior change.
 *
 * The `ManuallyAddPlayerModalContainer` test pins this value (asserts the query
 * fires with `first: 1000`); a revert to a mismatched limit fails that test.
 */
const NATIONALITIES_LIMIT = 1000;

/** Minimum character length for name fields */
const MIN_NAME_LENGTH = 2;

/** Pre-sorted constants list for the nationality dropdown (all entries have flag images) */
const SORTED_NATIONALITIES = [...nationalities].sort((a, b) =>
  a.name.localeCompare(b.name)
);

/** Max age for academy players */
const MAX_ACADEMY_AGE = 50;

/** Min age for academy players */
const MIN_ACADEMY_AGE = 10;

/**
 * Builds the validation schema, making age required when isAcademy is true
 * @param isAcademy - Whether the modal is in academy mode
 */
const buildSchema = (isAcademy: boolean) =>
  yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .required("Can't be blank")
      .min(MIN_NAME_LENGTH, `Min ${MIN_NAME_LENGTH} characters`),
    lastName: yup
      .string()
      .trim()
      .required("Can't be blank")
      .min(MIN_NAME_LENGTH, `Min ${MIN_NAME_LENGTH} characters`),
    positionIds: yup
      .array()
      .of(yup.string().required())
      .min(1, "Select at least one position")
      .required("Select at least one position"),
    currentTeam: yup.string().optional().default(""),
    currentTeamId: yup.string().optional().default(""),
    nationalityId: yup.string().optional().default(""),
    dateOfBirth: yup
      .string()
      .optional()
      .default("")
      .test("valid-date", "Invalid date", value => {
        if (!value) return true;

        return isValidDateString(value);
      })
      .test("not-future", "Date can't be in the future", value => {
        if (!value) return true;

        return !isFutureDate(value);
      }),
    age: isAcademy
      ? yup
          .string()
          .required("Age is required")
          .test(
            "valid-age",
            `Must be ${MIN_ACADEMY_AGE}–${MAX_ACADEMY_AGE}`,
            value => {
              if (!value) return false;
              const num = Number(value);
              return (
                Number.isInteger(num) &&
                num >= MIN_ACADEMY_AGE &&
                num <= MAX_ACADEMY_AGE
              );
            }
          )
      : yup.string().optional().default(""),
    grossAnnualSalary: yup
      .string()
      .optional()
      .default("")
      .test("valid-salary", "Must be a non-negative number", value => {
        if (!value) return true;
        const num = Number(value);
        return !isNaN(num) && num >= 0;
      }),
    marketValuation: yup
      .string()
      .optional()
      .default("")
      .test("valid-valuation", "Must be a non-negative number", value => {
        if (!value) return true;
        const num = Number(value);
        return !isNaN(num) && num >= 0;
      }),
    contractMonthsRemaining: yup
      .string()
      .optional()
      .default("")
      .test("valid-months", "Must be 0–120", value => {
        if (!value) return true;
        const num = Number(value);
        return Number.isInteger(num) && num >= 0 && num <= 120;
      }),
  });

/** Display strings for create mode */
const CREATE_MODE_STRINGS = {
  title: "Manually Add Player",
  description:
    "Add a player that isn't in the database. Required fields are marked with *.",
  submitLabel: "Add Player",
  submitLoadingLabel: "Adding...",
} as const;

/** Display strings for edit mode */
const EDIT_MODE_STRINGS = {
  title: "Edit Player",
  description: "Update the player's information below.",
  submitLabel: "Save Changes",
  submitLoadingLabel: "Saving...",
} as const;

/** Display strings for academy create mode */
const ACADEMY_MODE_STRINGS = {
  title: "Add Academy Player",
  description: "Add a new academy player. Required fields are marked with *.",
  submitLabel: "Add Player",
  submitLoadingLabel: "Adding...",
} as const;

/**
 * Builds form default values from editData (edit mode) or searchText (create mode)
 * @param editData - Pre-fill data for edit mode
 * @param defaultNames - Names parsed from search text for create mode
 * @param defaultNames.firstName - First name from search text
 * @param defaultNames.lastName - Last name from search text
 * @param defaultPositionIds - Position IDs to pre-select for academy mode
 */
const buildDefaultValues = (
  editData: ManualPlayerEditData | undefined,
  defaultNames: { firstName: string; lastName: string },
  defaultPositionIds?: readonly string[]
): IManuallyAddPlayerForm =>
  editData
    ? {
        firstName: editData.firstName,
        lastName: editData.lastName,
        positionIds: editData.positionIds,
        currentTeam: editData.currentTeam,
        currentTeamId: editData.currentTeamId,
        nationalityId: editData.nationalityId,
        dateOfBirth: editData.dateOfBirth,
        age: "",
        grossAnnualSalary: "",
        marketValuation: "",
        contractMonthsRemaining: "",
      }
    : {
        firstName: defaultNames.firstName,
        lastName: defaultNames.lastName,
        positionIds: defaultPositionIds ? [...defaultPositionIds] : [],
        currentTeam: "",
        currentTeamId: "",
        nationalityId: "",
        dateOfBirth: "",
        age: "",
        grossAnnualSalary: "",
        marketValuation: "",
        contractMonthsRemaining: "",
      };

/**
 * Container component for the Manually Add Player modal.
 * Handles form state, validation, dropdown data fetching, and mutation submission.
 * Supports create mode (default) and edit mode (when editData is provided).
 * @param props - Component props
 * @param props.isOpen - Whether the modal is visible
 * @param props.onClose - Callback to close the modal
 * @param props.searchText - Search text to pre-fill name fields (create mode)
 * @param props.editData - Pre-fill data for edit mode
 * @param props.onPlayerCreated - Optional callback after player creation, replaces default navigation
 * @param props.isAcademy - When true, sets isAcademy on the created player
 * @param props.defaultPositionIds - Position IDs to pre-select when opening from a position node
 */
const ManuallyAddPlayerModalContainer = ({
  isOpen,
  onClose,
  searchText,
  editData,
  onPlayerCreated,
  isAcademy,
  defaultPositionIds,
}: ManuallyAddPlayerModalContainerProps) => {
  // ====================
  // DEFINITIONS SECTION
  // ====================

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToastNotification();
  const router = useRouter();
  const isEditMode = editData !== undefined;
  const academyMode = isAcademy === true;
  const displayStrings = isEditMode
    ? EDIT_MODE_STRINGS
    : academyMode
      ? ACADEMY_MODE_STRINGS
      : CREATE_MODE_STRINGS;

  const schema = useMemo(() => buildSchema(academyMode), [academyMode]);

  // Pre-fill name from search text (create mode) or editData (edit mode)
  const defaultNames = useMemo(() => splitSearchText(searchText), [searchText]);
  const formDefaults = useMemo(
    () => buildDefaultValues(editData, defaultNames, defaultPositionIds),
    [editData, defaultNames, defaultPositionIds]
  );

  // Team autocomplete search state
  const [teamSearchText, setTeamSearchText] = useState("");
  const [debouncedTeamSearch, setDebouncedTeamSearch] = useState("");

  const debouncedSetTeamSearch = useDebouncedCallback((value: string) => {
    setDebouncedTeamSearch(value);
  }, TEAM_SEARCH_DEBOUNCE_MS);

  const handleTeamSearchChange = useCallback(
    (text: string) => {
      setTeamSearchText(text);
      debouncedSetTeamSearch(text);
    },
    [debouncedSetTeamSearch]
  );

  // Form state with react-hook-form + yup validation
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isValid, isSubmitted },
  } = useForm<IManuallyAddPlayerForm>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: formDefaults,
  });

  const handleTeamSuggestionSelect = useCallback(
    (team: IOptionSelect) => {
      setValue("currentTeamId", team.value);
      setTeamSearchText("");
      setDebouncedTeamSearch("");
    },
    [setValue]
  );

  // Fetch positions for dropdown. `$first` must cover the FULL taxonomy:
  // when omitted the backend returns an arbitrary 20-row default page — on
  // staging Goalkeeper is row 21 and was silently cut off, blocking manual
  // goalkeeper creation (SE-5095).
  const { data: positionData } = useRosterManagementListPositionsQuery({
    variables: { first: POSITIONS_FULL_LIST_FIRST },
    skip: !isOpen,
  });

  const positionOptions: readonly IOptionSelect[] = useMemo(
    () =>
      // Shared dedupe: the full list exposes legacy generic rows
      // (abbreviation: null) that duplicate specific positions (SE-5095).
      dedupePositionNodes(positionData?.listPositions?.edges).map(node => ({
        label: node.name,
        value: node.id,
      })),
    [positionData?.listPositions?.edges]
  );

  // Prefetch nationalities on mount so the dropdown is populated immediately when the modal opens
  const { data: nationalityData } = useRosterManagementListNationalitiesQuery({
    variables: { first: NATIONALITIES_LIMIT },
  });

  /** Map backend nationality name (lowercased) → ID for matching constants to backend */
  const backendIdByName: Record<string, string> = useMemo(
    () =>
      (nationalityData?.listNationalities?.edges ?? []).reduce(
        (acc, edge) => ({
          ...acc,
          [edge.node.name.toLowerCase()]: edge.node.id,
        }),
        {} as Record<string, string>
      ),
    [nationalityData?.listNationalities?.edges]
  );

  const nationalityOptions: readonly IOptionSelect[] = useMemo(
    () =>
      SORTED_NATIONALITIES.filter(
        nat => nat.name.toLowerCase() in backendIdByName
      ).map(nat => ({
        label: nat.name,
        value: backendIdByName[nat.name.toLowerCase()],
        imageUrl: flagUrlForNationality(nat, Platform.OS),
      })),
    [backendIdByName]
  );

  // Fetch team suggestions for autocomplete
  const { data: teamsData, loading: teamsLoading } =
    useRosterManagementListMyOrganizationsEligibleTeamsQuery({
      variables: {
        first: TEAM_SUGGESTIONS_LIMIT,
        search: debouncedTeamSearch || undefined,
      },
      skip: !isOpen || debouncedTeamSearch.length < 2,
    });

  const teamSuggestions: readonly IOptionSelect[] = useMemo(
    () =>
      teamsData?.listMyOrganizationsEligibleTeams?.edges
        ?.map(edge => {
          const teamName = edge.node.name ?? "";
          const leagueName = edge.node.league?.name;

          return {
            label: leagueName ? `${teamName} - ${leagueName}` : teamName,
            value: edge.node.id,
          };
        })
        .filter(team => team.label.trim()) ?? [],
    [teamsData?.listMyOrganizationsEligibleTeams?.edges]
  );

  const resetFormState = useCallback(() => {
    reset();
    setTeamSearchText("");
    setDebouncedTeamSearch("");
  }, [reset]);

  /** Shared mutation input fields derived from form data */
  const buildMutationInput = useCallback(
    (formData: IManuallyAddPlayerForm) => {
      // For academy mode, derive dateOfBirth from age if no explicit DOB
      // Use today's date minus age years so the derived age matches the entered value
      const now = new Date();
      const dateOfBirth =
        formData.dateOfBirth ||
        (academyMode && formData.age
          ? `${now.getFullYear() - Number(formData.age)}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
          : undefined);
      return {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        positionIds:
          formData.positionIds.length > 0 ? formData.positionIds : undefined,
        teamName: extractTeamName(formData.currentTeam),
        nationalityId: formData.nationalityId || undefined,
        dateOfBirth,
        ...(academyMode ? { isAcademy: true } : {}),
      };
    },
    [academyMode]
  );

  const successVerb = isEditMode ? "updated" : "created";
  const failureVerb = isEditMode ? "update" : "create";

  /** Shared mutation callback for both create and update */
  const onMutationCompleted = useCallback(() => {
    toast.show({
      action: "success",
      description: `Player "${getValues("firstName")} ${getValues("lastName")}" ${successVerb} successfully`,
    });
    resetFormState();
    onClose();
  }, [toast, getValues, successVerb, resetFormState, onClose]);

  /**
   * Create-specific completion: shows toast, closes modal, then navigates to the
   * new player's profile so the user doesn't have to search for them manually.
   */
  const onCreateCompleted = useCallback(
    (data: { createManualPlayer: { id: string } }) => {
      const playerName =
        `${getValues("firstName")} ${getValues("lastName")}`.trim();
      onMutationCompleted();
      if (onPlayerCreated) {
        onPlayerCreated(data.createManualPlayer.id, playerName);
      } else {
        router.push(`/players/${data.createManualPlayer.id}`);
      }
    },
    [onMutationCompleted, onPlayerCreated, getValues, router]
  );

  const onMutationError = useCallback(
    (error: { message: string }) => {
      toast.show({
        action: "danger",
        description: error.message || `Failed to ${failureVerb} player`,
      });
    },
    [toast, failureVerb]
  );

  // Upsert organization player data (salary) after academy player creation
  const [upsertOrgPlayerData] =
    useSquadPlanningUpsertOrganizationPlayerDataMutation({
      onError: (error: { message: string }) => {
        toast.show({
          action: "danger",
          description: error.message || "Failed to save salary",
        });
      },
    });

  /**
   * After academy player creation, upsert salary if provided.
   * Wraps onCreateCompleted with the salary side-effect.
   */
  const onCreateCompletedWithSalary = useCallback(
    (data: { createManualPlayer: { id: string } }) => {
      const salary = getValues("grossAnnualSalary");
      const valuation = getValues("marketValuation");
      if (academyMode && (salary || valuation)) {
        void upsertOrgPlayerData({
          variables: {
            input: {
              playerId: data.createManualPlayer.id,
              ...(salary ? { grossAnnualSalary: Number(salary) } : {}),
              ...(valuation ? { estValuation: Number(valuation) } : {}),
            },
          },
        });
      }
      onCreateCompleted(data);
    },
    [academyMode, getValues, upsertOrgPlayerData, onCreateCompleted]
  );

  // Create manual player mutation
  const [createManualPlayer, { loading: createLoading }] =
    usePlayerSearchCreateManualPlayerMutation({
      onCompleted: onCreateCompletedWithSalary,
      onError: onMutationError,
    });

  // Update manual player mutation — refetches player detail to refresh the page
  const [updateManualPlayer, { loading: updateLoading }] =
    usePlayerDetailUpdateManualPlayerMutation({
      refetchQueries: ["PlayerDetailGetPlayer"],
      onCompleted: onMutationCompleted,
      onError: onMutationError,
    });

  const isSubmitting = isEditMode ? updateLoading : createLoading;

  const submit = useCallback(
    (formData: IManuallyAddPlayerForm) => {
      if (isEditMode && editData) {
        const changedFields = buildChangedFieldsInput(formData, editData);
        updateManualPlayer({
          variables: { input: { id: editData.playerId, ...changedFields } },
        });
      } else {
        const input = buildMutationInput(formData);
        createManualPlayer({
          variables: { input },
        });
      }
    },
    [
      buildMutationInput,
      isEditMode,
      editData,
      updateManualPlayer,
      createManualPlayer,
    ]
  );

  const doSubmit = useMemo(() => handleSubmit(submit), [handleSubmit, submit]);

  const handleClose = useCallback(() => {
    resetFormState();
    onClose();
  }, [resetFormState, onClose]);

  // ====================
  // SIDE EFFECTS SECTION
  // ====================

  // Reset form with correct defaults when modal opens or editData changes
  useEffect(() => {
    if (isOpen) {
      reset(formDefaults);
    }
  }, [isOpen, formDefaults, reset]);

  // ====================
  // RETURN SECTION
  // ====================

  return (
    <ManuallyAddPlayerModalView
      isOpen={isOpen}
      isDark={isDark}
      title={displayStrings.title}
      description={displayStrings.description}
      submitLabel={displayStrings.submitLabel}
      submitLoadingLabel={displayStrings.submitLoadingLabel}
      isValid={isValid}
      isSubmitting={isSubmitting}
      isSubmitted={isSubmitted}
      control={control}
      errors={errors}
      positionOptions={positionOptions}
      nationalityOptions={nationalityOptions}
      teamSuggestions={teamSuggestions}
      teamSuggestionsLoading={teamsLoading}
      teamSearchText={teamSearchText}
      onClose={handleClose}
      onSubmit={doSubmit}
      onTeamSearchChange={handleTeamSearchChange}
      onTeamSuggestionSelect={handleTeamSuggestionSelect}
      isAcademy={academyMode}
    />
  );
};

export default ManuallyAddPlayerModalContainer;
